/** @jsx isolateComponent */

import { useContext, useEffect, useState } from 'react';

import { ChangePlan } from '@zapier/checkout';
import { Modal } from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';

import { Error } from '@zapier/upsells/src/components/Error';

import { UpgradeModalContext } from '@zapier/upsells/src/contexts';
import { canUpgradeForReason } from '@zapier/upsells/src/utils';

import { UpgradeModalProps } from '../UpgradeModal';

import { Celebrate, ExtendTrial, ModalContent, UpgradeNudge } from './';

export const MODAL_STATE = {
  celebrateTrial: 'celebrateTrial',
  celebrateUpgrade: 'celebrateUpgrade',
  error: 'error',
  extendTrial: 'extendTrial',
  nudge: 'nudge',
  upgrade: 'upgrade',
};

export const UpgradeModalLayout = ({
  appName,
  onClose,
  onUpgradeFinish = () => {},
  shouldAutoUpgrade = true,
  upgradeReason,
  wasTriggeredByZapToggle = false,
}: UpgradeModalProps) => {
  const {
    account,
    accountCurrency,
    canExtendTrial,
    destinationPlan,
    error,
    extendTrial,
    isLoading,
  } = useContext(UpgradeModalContext);

  const [modalState, setModalState] = useState(MODAL_STATE.nudge);

  useEffect(() => {
    if (error) {
      setModalState(MODAL_STATE.error);
    }
  }, [error]);

  const isCelebrationState =
    modalState === MODAL_STATE.celebrateTrial ||
    modalState === MODAL_STATE.celebrateUpgrade;

  if (
    isLoading ||
    !destinationPlan ||
    (!isCelebrationState && // Celebration state happens after upgrade, so canUpgradeForReason will return false and prevent the modal from rendering. We don't want that.
      !canUpgradeForReason({
        plan: account?.plan,
        upgradeReason,
      }))
  ) {
    return null;
  }

  const onCloseWithReload = () => {
    onClose();

    // Refresh page to load new plan information. Remove querystring params.
    window.location.href = window.location.origin + window.location.pathname;
  };

  const onExtendTrialClick = async () => {
    setModalState(MODAL_STATE.extendTrial);
  };

  const onExtendTrialComplete = async () => {
    try {
      await extendTrial();
      setModalState(MODAL_STATE.celebrateTrial);
    } catch {
      setModalState(MODAL_STATE.error);
    }
  };

  const onUpgradeClick = () => {
    setModalState(MODAL_STATE.upgrade);
  };

  const onUpgradeComplete = () => {
    onUpgradeFinish();
    setModalState(MODAL_STATE.celebrateUpgrade);
  };

  const celebrateButtonText = wasTriggeredByZapToggle
    ? 'Back to Zap'
    : 'Continue';

  const contentMap = {
    [MODAL_STATE.nudge]: (
      <UpgradeNudge
        appName={appName}
        canExtendTrial={canExtendTrial}
        destinationPlan={destinationPlan}
        onClose={onClose}
        onExtendTrialClick={onExtendTrialClick}
        onUpgradeClick={onUpgradeClick}
        upgradeReason={upgradeReason}
        wasTriggeredByZapToggle={wasTriggeredByZapToggle}
      />
    ),
    [MODAL_STATE.upgrade]: (
      <ChangePlan
        changeType="upgrade"
        currency={account?.currency ?? undefined}
        isEmbeddedInInlineUpgrade={true}
        onCancel={onClose}
        onFinishChangePlan={onUpgradeComplete}
        showSuccessModal={false}
        toPlanName={destinationPlan?.name?.toLowerCase() ?? ''}
      />
    ),
    [MODAL_STATE.celebrateTrial]: (
      <Celebrate
        buttonText={celebrateButtonText}
        celebrationType="trial"
        onClose={onCloseWithReload}
      />
    ),
    [MODAL_STATE.celebrateUpgrade]: (
      <Celebrate
        buttonText={celebrateButtonText}
        celebrationType="upgrade"
        newPlan={account?.plan} // Current plan has been upgraded at this point, so don't use `destinationPlan` here.
        onClose={onCloseWithReload}
      />
    ),
    [MODAL_STATE.error]: (
      <Error
        eventName="upgrade_modal_error_view"
        fullStoryEventName="Viewed Upgrade Modal - Error View"
        message={
          error ??
          "Please try again. If the problem persists, click the button below and we'll help you through it."
        }
      />
    ),
    [MODAL_STATE.extendTrial]: (
      <ExtendTrial
        accountCurrency={accountCurrency}
        onClose={onCloseWithReload}
        onExtendTrial={onExtendTrialComplete}
        shouldAutoUpgrade={shouldAutoUpgrade}
        trialPlan={destinationPlan!}
        upgradeReason={upgradeReason}
      />
    ),
  };

  const closeModal =
    modalState === MODAL_STATE.celebrateTrial ||
    modalState === MODAL_STATE.celebrateUpgrade
      ? onCloseWithReload
      : onClose;

  return (
    <Modal onClosed={closeModal}>
      <ModalContent modalState={modalState}>
        {contentMap[modalState]}
      </ModalContent>
    </Modal>
  );
};
